<template>
    <!-- Teacher Details Modal -->
    <div v-if="modalOpen" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div class="flex items-end justify-center min-h-screen min-w-0 pt-4 px-2 sm:px-4 pb-24 text-center sm:block sm:p-0 sm:pb-20">
        <div class="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-80" @click="closeModalInternal"></div>
        <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl dark:shadow-2xl transform transition-all sm:my-8 sm:align-middle w-full max-w-[calc(100vw-0.5rem)] sm:max-w-4xl sm:w-full max-h-[85vh] sm:max-h-[90vh] overflow-y-auto overscroll-y-contain touch-pan-y">
          <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-16 w-16 rounded-full overflow-hidden bg-blue-100 dark:bg-blue-900/30 sm:mx-0 sm:h-16 sm:w-16 ring-4 ring-white dark:ring-gray-700"
                   :class="showTeacherProfilePhoto ? 'bg-white dark:bg-gray-700' : 'bg-blue-100 dark:bg-blue-900/30'">
                <img
                  v-if="showTeacherProfilePhoto"
                  :src="teacherProfilePhotoSrc"
                  :alt="selectedTeacher?.teacher_name || 'Teacher'"
                  class="w-full h-full object-cover"
                  @load="onTeacherProfilePhotoLoad"
                  @error="profilePictureBroken = true"
                />
                <svg v-else class="h-8 w-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                  Teacher Details
                </h3>
                <div v-if="selectedTeacher" class="mt-4 space-y-6">
                  <!-- Basic Teacher Information -->
                  <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4 flex items-center">
                      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                      </svg>
                      Personal Information
                    </h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Full Name</label>
                        <p class="mt-1 text-sm font-semibold text-blue-900 dark:text-blue-100">{{ selectedTeacher.teacher_name }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Teacher ID</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100 font-mono">{{ selectedTeacher.teacher_ID || 'Not assigned' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Father's / Mother's Name</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">{{ (selectedTeacher as any).father_name || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Date of Birth</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">{{ formatDate(selectedTeacher.date_of_birth) }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Joining Date</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">{{ formatDate(selectedTeacher.joining_date) }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Appointment Date</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">{{ (selectedTeacher as any).appointment_date ? formatDate((selectedTeacher as any).appointment_date) : 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Gender</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">
                          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                            {{ selectedTeacher.gender }}
                          </span>
                        </p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Social Group</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">{{ selectedTeacher.social_group || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Religion</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">{{ selectedTeacher.religion || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Aadhaar Number</label>
                        <p class="mt-1 text-sm font-mono text-blue-900 dark:text-blue-100 bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded">{{ selectedTeacher.aadhaar_number || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Service Category</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">
                          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                            {{ formatDisplayText(selectedTeacher.service_category ?? '') || 'Not specified' }}
                          </span>
                        </p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Designation</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">
                          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                            {{ (selectedTeacher as any).designation || 'Not specified' }}
                          </span>
                        </p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Type of Appointment</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">
                          <span class="inline-flex items-center gap-2">
                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                              {{ (selectedTeacher as any).appointment_type || 'Not specified' }}
                            </span>
                            <button
                              v-if="canChangeAppointmentType"
                              type="button"
                              class="text-xs font-semibold px-2 py-1 rounded-md bg-white dark:bg-gray-700 border border-blue-200 dark:border-blue-600 text-blue-700 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                              @click="openChangeAppointmentTypeModal"
                            >
                              Change Appointment Type
                            </button>
                          </span>
                        </p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Blood Group</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">
                          <span v-if="(selectedTeacher as any).blood_group" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                            {{ (selectedTeacher as any).blood_group }}
                          </span>
                          <span v-else class="text-gray-500">Not specified</span>
                        </p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-blue-700 dark:text-blue-300">Identification Mark</label>
                        <p class="mt-1 text-sm text-blue-900 dark:text-blue-100">
                          <span v-if="(selectedTeacher as any).identification_mark">{{ (selectedTeacher as any).identification_mark }}</span>
                          <span v-else class="text-gray-500">Not specified</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Contact Information -->
                  <div class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-green-900 dark:text-green-100 mb-4 flex items-center">
                      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                      </svg>
                      Contact Information
                    </h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="text-sm font-medium text-green-700 dark:text-green-300">Phone Number</label>
                        <p class="mt-1 text-sm text-green-900 dark:text-green-100">
                          <span v-if="selectedTeacher.phone_number" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200">
                            {{ selectedTeacher.phone_number }}
                          </span>
                          <span v-else class="text-gray-500">Not specified</span>
                        </p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-green-700 dark:text-green-300">Email Address</label>
                        <p class="mt-1 text-sm text-green-900 dark:text-green-100">
                          <span v-if="selectedTeacher.email" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200">
                            {{ selectedTeacher.email }}
                          </span>
                          <span v-else class="text-gray-500">Not specified</span>
                        </p>
                      </div>
                      <div class="md:col-span-2">
                        <label class="text-sm font-medium text-green-700 dark:text-green-300">Permanent Address</label>
                        <p class="mt-1 text-sm text-green-900 dark:text-green-100">{{ (selectedTeacher as any).permanent_address || 'Not specified' }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- School Information -->
                  <div class="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-4 flex items-center">
                      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                      </svg>
                      School Information
                    </h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="text-sm font-medium text-purple-700 dark:text-purple-300">School Name</label>
                        <p class="mt-1 text-sm text-purple-900 dark:text-purple-100">{{ (selectedTeacher as any).schools?.school_name || selectedTeacher.current_school_name || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-purple-700 dark:text-purple-300">School ID</label>
                        <p class="mt-1 text-sm font-mono text-purple-900 dark:text-purple-100 bg-purple-100 dark:bg-purple-800 px-2 py-1 rounded">{{ selectedTeacher.school_id || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-purple-700 dark:text-purple-300">School Level</label>
                        <p class="mt-1 text-sm text-purple-900 dark:text-purple-100">{{ selectedTeacher.school_level || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-purple-700 dark:text-purple-300">Management Type</label>
                        <p class="mt-1 text-sm text-purple-900 dark:text-purple-100">
                          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200">
                            {{ formatDisplayText(selectedTeacher.management) || 'Not specified' }}
                          </span>
                        </p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-purple-700 dark:text-purple-300">School Phone</label>
                        <p class="mt-1 text-sm text-purple-900 dark:text-purple-100">
                          <span v-if="selectedTeacher.school_phone" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200">
                            {{ selectedTeacher.school_phone }}
                          </span>
                          <span v-else class="text-gray-500">Not specified</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Teaching Information -->
                  <div class="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-4 flex items-center">
                      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                      </svg>
                      Teaching Information
                    </h4>
                    <div class="mb-4 rounded-xl border border-orange-200/80 bg-white/70 px-3 py-3 shadow-sm dark:border-orange-800/50 dark:bg-orange-950/25 sm:px-4">
                      <label class="block text-sm font-medium text-orange-800 dark:text-orange-200">Professional qualification</label>
                      <div
                        v-if="selectedTeacherProfessionalQualificationLabels.length"
                        class="mt-2 flex flex-wrap items-center gap-2"
                      >
                        <span
                          v-for="(label, pqIdx) in selectedTeacherProfessionalQualificationLabels"
                          :key="pqIdx"
                          class="inline-flex max-w-full items-center rounded-full bg-gradient-to-r from-orange-100 to-amber-100 px-3 py-1.5 text-xs font-semibold leading-tight text-orange-950 shadow-sm ring-1 ring-orange-200/90 dark:from-orange-900/60 dark:to-amber-900/40 dark:text-orange-50 dark:ring-orange-600/40"
                        >
                          <span class="truncate" :title="label">{{ label }}</span>
                        </span>
                      </div>
                      <p v-else class="mt-2 text-sm text-gray-500 dark:text-gray-400">Not specified</p>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Subjects Taught</label>
                        <p class="mt-1 text-sm text-orange-900 dark:text-orange-100">{{ formatSubjects(selectedTeacher.subjects_taught) || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Classes Taught</label>
                        <p class="mt-1 text-sm text-orange-900 dark:text-orange-100">{{ formatClasses(selectedTeacher.classes_taught) || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Medium of Institution</label>
                        <p class="mt-1 text-sm text-orange-900 dark:text-orange-100">
                          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-200">
                            {{ selectedTeacher.medium || 'Not specified' }}
                          </span>
                        </p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Experience</label>
                        <p class="mt-1 text-sm text-orange-900 dark:text-orange-100">{{ getExperienceYears(selectedTeacher) }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Appointment Date</label>
                        <p class="mt-1 text-sm text-orange-900 dark:text-orange-100">{{ (selectedTeacher as any).appointment_date ? formatDate((selectedTeacher as any).appointment_date) : 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Merit Rank</label>
                        <p class="mt-1 text-sm text-orange-900 dark:text-orange-100">
                          <span v-if="(selectedTeacher as any).merit_rank" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-200">
                            {{ (selectedTeacher as any).merit_rank }}
                          </span>
                          <span v-else class="text-gray-500">Not specified</span>
                        </p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Seniority Score</label>
                        <p class="mt-1 text-sm text-orange-900 dark:text-orange-100">
                          <span v-if="(selectedTeacher as any).seniority_score" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-200 font-mono">
                            {{ (selectedTeacher as any).seniority_score }}
                          </span>
                          <span v-else class="text-gray-500">Not specified</span>
                        </p>
                      </div>
                      <div class="md:col-span-2">
                        <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Remarks</label>
                        <p class="mt-1 text-sm text-orange-900 dark:text-orange-100">{{ (selectedTeacher as any).remarks || 'Not specified' }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Education Qualifications -->
                  <div class="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-amber-900 dark:text-amber-100 mb-4 flex items-center justify-between">
                      <div class="flex items-center">
                        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                        </svg>
                        Education Qualifications
                      </div>
                      <button
                        v-if="teacherEducationQualifications.length > 0"
                        @click="showEducationQualificationsModal = true"
                        class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-800 hover:bg-amber-200 dark:hover:bg-amber-700 rounded-lg transition-colors"
                      >
                        <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                        </svg>
                        View All ({{ teacherEducationQualifications.length }})
                      </button>
                    </h4>
                    <div v-if="loadingEducationQualifications" class="text-center py-4">
                      <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
                      <p class="mt-2 text-sm text-amber-700 dark:text-amber-300">Loading qualifications...</p>
                    </div>
                    <div v-else-if="teacherEducationQualifications.length > 0" class="space-y-3">
                      <div 
                        v-for="(qualification, index) in teacherEducationQualifications.slice(0, 2)" 
                        :key="qualification.id ?? index"
                        class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700"
                      >
                        <div class="flex flex-col gap-3">
                          <div class="flex items-start justify-between gap-2 flex-wrap">
                            <div class="flex-1 min-w-0">
                              <p class="text-sm font-semibold text-amber-900 dark:text-amber-100">{{ qualification.degree_name }}</p>
                              <p class="text-xs text-amber-700 dark:text-amber-300 mt-1">{{ qualification.institution_name }}</p>
                              <p class="text-xs text-amber-600 dark:text-amber-400 mt-1">Year: {{ qualification.year_of_passing }}</p>
                              <div class="flex flex-wrap gap-1.5 mt-2">
                                <span
                                  v-if="qualification.is_verified"
                                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200"
                                >
                                  Verified
                                </span>
                                <span
                                  v-else-if="qualification.admin_rejection_remarks"
                                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200"
                                >
                                  Rejected
                                </span>
                                <span
                                  v-else
                                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100"
                                >
                                  Pending verification
                                </span>
                              </div>
                            </div>
                          </div>
                          <div class="flex flex-col sm:flex-row flex-wrap gap-2 w-full">
                            <button
                              type="button"
                              @click="viewEducationQualification(qualification)"
                              class="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-800 hover:bg-amber-200 dark:hover:bg-amber-700 rounded-lg transition-colors w-full sm:w-auto sm:flex-1"
                            >
                              View Details
                            </button>
                            <template v-if="canVerifyEducationQualifications && !qualification.is_verified">
                              <button
                                v-if="!qualification.admin_rejection_remarks"
                                type="button"
                                :disabled="verifyingEducationQualId === qualification.id || rejectingEducationQualId === qualification.id"
                                class="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 w-full sm:w-auto sm:flex-1"
                                @click="verifyTeacherEducationQualification(qualification)"
                              >
                                {{ verifyingEducationQualId === qualification.id ? 'Verifyingâ€¦' : 'Approve qualification' }}
                              </button>
                              <button
                                type="button"
                                :disabled="verifyingEducationQualId === qualification.id || rejectingEducationQualId === qualification.id"
                                class="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 w-full sm:w-auto sm:flex-1"
                                @click="openRejectEducationQualificationModal(qualification)"
                              >
                                {{
                                  qualification.admin_rejection_remarks
                                    ? 'Update rejection'
                                    : 'Reject qualification'
                                }}
                              </button>
                            </template>
                          </div>
                        </div>
                      </div>
                      <div v-if="teacherEducationQualifications.length > 2" class="text-center pt-2">
                        <button
                          @click="showEducationQualificationsModal = true"
                          class="text-sm font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100"
                        >
                          + {{ teacherEducationQualifications.length - 2 }} more qualification(s)
                        </button>
                      </div>
                    </div>
                    <div v-else class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                      <p class="text-sm text-gray-600 dark:text-gray-400">No education qualifications recorded</p>
                    </div>
                  </div>

                  <!-- Training Details -->
                  <div class="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-indigo-900 dark:text-indigo-100 mb-4 flex items-center justify-between">
                      <div class="flex items-center">
                        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                        </svg>
                        Training Details
                      </div>
                      <button
                        v-if="teacherTrainings.length > 0"
                        @click="showTrainingsModal = true"
                        class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-800 hover:bg-indigo-200 dark:hover:bg-indigo-700 rounded-lg transition-colors"
                      >
                        <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                        </svg>
                        View All ({{ teacherTrainings.length }})
                      </button>
                    </h4>
                    <div v-if="loadingTrainings" class="text-center py-4">
                      <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                      <p class="mt-2 text-sm text-indigo-700 dark:text-indigo-300">Loading trainings...</p>
                    </div>
                    <div v-else-if="teacherTrainings.length > 0" class="space-y-3">
                      <div 
                        v-for="(training, index) in (teacherTrainings.length > 3 && !teacherDetailsTrainingExpanded ? teacherTrainings.slice(0, 3) : teacherTrainings)" 
                        :key="index"
                        class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-indigo-200 dark:border-indigo-700"
                      >
                        <div class="flex items-start justify-between">
                          <div class="flex-1">
                            <p class="text-sm font-semibold text-indigo-900 dark:text-indigo-100">{{ training.training_name }}</p>
                            <p class="text-xs text-indigo-700 dark:text-indigo-300 mt-1">{{ training.provider_name }}</p>
                            <p class="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                              {{ formatTrainingDate(training.start_date) }} 
                              <span v-if="training.end_date"> - {{ formatTrainingDate(training.end_date) }}</span>
                            </p>
                            <p v-if="training.duration" class="text-xs text-indigo-600 dark:text-indigo-400 mt-1">Duration: {{ training.duration }}</p>
                          </div>
                          <button
                            @click="viewTraining(training)"
                            class="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-800 hover:bg-indigo-200 dark:hover:bg-indigo-700 rounded transition-colors"
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                      <div v-if="teacherTrainings.length > 3" class="flex justify-center pt-2 border-t border-indigo-200 dark:border-indigo-700 mt-3">
                        <button
                          @click="teacherDetailsTrainingExpanded = !teacherDetailsTrainingExpanded"
                          class="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center justify-center gap-2 px-4 py-2 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                        >
                          <template v-if="!teacherDetailsTrainingExpanded">
                            <span>Show {{ teacherTrainings.length - 3 }} more training{{ teacherTrainings.length - 3 > 1 ? 's' : '' }}</span>
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                          </template>
                          <template v-else>
                            <span>Show less</span>
                            <svg class="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                          </template>
                        </button>
                      </div>
                    </div>
                    <div v-else class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-indigo-200 dark:border-indigo-700">
                      <p class="text-sm text-gray-600 dark:text-gray-400">No training details recorded</p>
                    </div>
                  </div>

                  <!-- Badges Information -->
                  <div class="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-4 flex items-center">
                      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
                      </svg>
                      Badges & Awards
                    </h4>
                    <div class="space-y-4">
                      <!-- Priority Badges -->
                      <div v-if="teacherPriorityBadges.length > 0">
                        <label class="text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-2 block">Priority Badges</label>
                        <div class="space-y-2">
                          <div v-for="badge in (teacherPriorityBadges.length > 3 && !teacherDetailsBadgesExpanded ? teacherPriorityBadges.slice(0, 3) : teacherPriorityBadges)" :key="badge.id" class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-yellow-200 dark:border-yellow-700">
                            <div class="flex items-center justify-between">
                              <div>
                                <p class="text-sm font-semibold text-yellow-900 dark:text-yellow-100">{{ badge.priority_badge_types?.name || 'Badge' }}</p>
                                <p v-if="badge.valid_until" class="text-xs text-yellow-700 dark:text-yellow-300">
                                  Valid until: {{ formatDate(badge.valid_until) }}
                                </p>
                                <p v-if="badge.award_year" class="text-xs text-yellow-700 dark:text-yellow-300">
                                  Award Year: {{ badge.award_year }}
                                </p>
                              </div>
                              <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                âœ“ Approved
                              </span>
                            </div>
                          </div>
                        </div>
                        <div v-if="teacherPriorityBadges.length > 3" class="flex justify-center pt-2 mt-2 border-t border-yellow-200 dark:border-yellow-700">
                          <button
                            @click="teacherDetailsBadgesExpanded = !teacherDetailsBadgesExpanded"
                            class="text-sm font-medium text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300 flex items-center justify-center gap-2 px-4 py-2 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
                          >
                            <template v-if="!teacherDetailsBadgesExpanded">
                              <span>Show {{ teacherPriorityBadges.length - 3 }} more badge{{ teacherPriorityBadges.length - 3 > 1 ? 's' : '' }}</span>
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                              </svg>
                            </template>
                            <template v-else>
                              <span>Show less</span>
                              <svg class="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                              </svg>
                            </template>
                          </button>
                        </div>
                      </div>
                      <div v-else class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-yellow-200 dark:border-yellow-700">
                        <p class="text-sm text-gray-600 dark:text-gray-400">No priority badges</p>
                      </div>

                      <!-- MLA Spouse Badges -->
                      <div v-if="mlaBadgeMenuEnabled">
                        <div v-if="teacherMLABadges.length > 0">
                          <label class="text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-2 block">MLA Spouse Badges</label>
                          <div class="space-y-2">
                            <div v-for="badge in (teacherMLABadges.length > 3 && !teacherDetailsBadgesExpanded ? teacherMLABadges.slice(0, 3) : teacherMLABadges)" :key="badge.id" class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-yellow-200 dark:border-yellow-700">
                              <div class="flex items-center justify-between">
                                <div>
                                  <p class="text-sm font-semibold text-yellow-900 dark:text-yellow-100">{{ badge.mla_name }}</p>
                                  <p v-if="badge.mla_constituency" class="text-xs text-yellow-700 dark:text-yellow-300">
                                    Constituency: {{ badge.mla_constituency }}
                                  </p>
                                  <p class="text-xs text-yellow-700 dark:text-yellow-300">
                                    Relationship: {{ badge.relationship }}
                                  </p>
                                  <p v-if="badge.valid_until" class="text-xs text-yellow-700 dark:text-yellow-300">
                                    Valid until: {{ formatDate(badge.valid_until) }}
                                  </p>
                                </div>
                                <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  âœ“ Approved
                                </span>
                              </div>
                            </div>
                          </div>
                          <div v-if="teacherMLABadges.length > 3" class="flex justify-center pt-2 mt-2 border-t border-yellow-200 dark:border-yellow-700">
                            <button
                              @click="teacherDetailsBadgesExpanded = !teacherDetailsBadgesExpanded"
                              class="text-sm font-medium text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300 flex items-center justify-center gap-2 px-4 py-2 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
                            >
                              <template v-if="!teacherDetailsBadgesExpanded">
                                <span>Show {{ teacherMLABadges.length - 3 }} more badge{{ teacherMLABadges.length - 3 > 1 ? 's' : '' }}</span>
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                              </template>
                              <template v-else>
                                <span>Show less</span>
                                <svg class="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                              </template>
                            </button>
                          </div>
                        </div>
                        <div v-else class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-yellow-200 dark:border-yellow-700">
                          <p class="text-sm text-gray-600 dark:text-gray-400">No MLA spouse badges</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Location Information -->
                  <div class="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-indigo-900 dark:text-indigo-100 mb-4 flex items-center">
                      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                      Location Information
                    </h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="text-sm font-medium text-indigo-700 dark:text-indigo-300">District</label>
                        <p class="mt-1 text-sm text-indigo-900 dark:text-indigo-100">{{ selectedTeacher.district || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-indigo-700 dark:text-indigo-300">Block Office</label>
                        <p class="mt-1 text-sm text-indigo-900 dark:text-indigo-100">{{ selectedTeacher.block_office || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-indigo-700 dark:text-indigo-300">Habitation</label>
                        <p class="mt-1 text-sm text-indigo-900 dark:text-indigo-100">{{ selectedTeacher.habitation || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-indigo-700 dark:text-indigo-300">Pin Code</label>
                        <p class="mt-1 text-sm text-indigo-900 dark:text-indigo-100">{{ selectedTeacher.pincode || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-indigo-700 dark:text-indigo-300">RD Block</label>
                        <p class="mt-1 text-sm text-indigo-900 dark:text-indigo-100">{{ selectedTeacher.rd_block || 'Not specified' }}</p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-indigo-700 dark:text-indigo-300">Area Type</label>
                        <p class="mt-1 text-sm text-indigo-900 dark:text-indigo-100">
                          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200">
                            {{ selectedTeacher.habitation_class || 'Not specified' }}
                          </span>
                        </p>
                      </div>
                      <div>
                        <label class="text-sm font-medium text-indigo-700 dark:text-indigo-300">Habitation Category</label>
                        <p class="mt-1 text-sm text-indigo-900 dark:text-indigo-100">
                          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200">
                            {{ formatDisplayText(selectedTeacher.habitation_category ?? '') || 'Not specified' }}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Posting History -->
                  <div v-if="selectedTeacher.posting_histories && selectedTeacher.posting_histories.length > 0" class="bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-cyan-900 dark:text-cyan-100 mb-4 flex items-center">
                      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                      </svg>
                      Posting History
                    </h4>
                    <div class="space-y-3">
                      <div v-for="(posting, index) in (selectedTeacher.posting_histories.length > 3 && !teacherDetailsPostingHistoryExpanded ? selectedTeacher.posting_histories.slice(0, 3) : selectedTeacher.posting_histories)" :key="index" class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-cyan-200 dark:border-cyan-700">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label class="text-sm font-medium text-cyan-700 dark:text-cyan-300">School</label>
                            <p class="text-sm text-cyan-900 dark:text-cyan-100">{{ posting.school_name }}</p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-cyan-700 dark:text-cyan-300">Period</label>
                            <p class="text-sm text-cyan-900 dark:text-cyan-100">{{ formatDate(posting.from_date) }} - {{ posting.to_date ? formatDate(posting.to_date) : 'Current' }}</p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-cyan-700 dark:text-cyan-300">Status</label>
                            <p class="text-sm text-cyan-900 dark:text-cyan-100">
                              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200">
                                {{ posting.status || 'Active' }}
                              </span>
                            </p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-cyan-700 dark:text-cyan-300">Appointment Type</label>
                            <p class="text-sm text-cyan-900 dark:text-cyan-100">
                              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 dark:bg-cyan-800 text-cyan-800 dark:text-cyan-200">
                                {{ (posting as any).appointment_types?.name || (posting as any).appointment_type || 'Legacy/Unknown' }}
                              </span>
                            </p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-cyan-700 dark:text-cyan-300">Location</label>
                            <p class="text-sm text-cyan-900 dark:text-cyan-100">{{ posting.district }}, {{ posting.block_office }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- More/Less button for expandable posting history -->
                    <div v-if="selectedTeacher.posting_histories.length > 3" class="flex justify-center pt-3 mt-3 border-t border-cyan-200 dark:border-cyan-700">
                      <button 
                        @click="teacherDetailsPostingHistoryExpanded = !teacherDetailsPostingHistoryExpanded"
                        class="text-sm font-medium text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300 flex items-center justify-center gap-2 px-4 py-2 rounded-lg hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-colors"
                      >
                        <template v-if="!teacherDetailsPostingHistoryExpanded">
                          <span>Show {{ selectedTeacher.posting_histories.length - 3 }} more posting{{ selectedTeacher.posting_histories.length - 3 > 1 ? 's' : '' }}</span>
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </template>
                        <template v-else>
                          <span>Show less</span>
                          <svg class="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </template>
                      </button>
                    </div>
                  </div>

                  <!-- Appointment Type Timeline (same card layout as Posting History "Full appointment type timeline" modal) -->
                  <div class="bg-slate-50 dark:bg-slate-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center">
                      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      Appointment Type Timeline
                    </h4>
                    <div v-if="loadingAppointmentTypeHistory" class="text-sm text-gray-600 dark:text-gray-300">
                      Loading…
                    </div>
                    <div v-else-if="appointmentTypeTimelineDisplayRows.length === 0" class="text-sm text-gray-600 dark:text-gray-400">
                      No appointment type changes recorded.
                    </div>
                    <ul v-else class="space-y-2">
                      <li
                        v-for="r in appointmentTypeTimelineDisplayRows"
                        :key="`apt-${r.id}-${r.schoolName}-${r.date}`"
                        class="flex items-start justify-between gap-4 rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30 px-3 py-2"
                      >
                        <div class="min-w-0">
                          <div class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {{ r.name }}
                          </div>
                          <div class="text-xs text-gray-600 dark:text-gray-300 truncate">
                            {{ r.schoolName }}
                          </div>
                          <div class="text-[11px] text-gray-500 dark:text-gray-400">
                            Source: {{ r.source }}
                          </div>
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {{ formatDateOnlyForDisplay(r.date) || 'Not Available' }}
                        </div>
                      </li>
                    </ul>
                  </div>

                  <!-- Deputations -->
                  <div v-if="selectedTeacher.deputations && selectedTeacher.deputations.length > 0" class="bg-pink-50 dark:bg-pink-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-pink-900 dark:text-pink-100 mb-4 flex items-center">
                      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                      </svg>
                      Deputation History
                    </h4>
                    <div class="space-y-3">
                      <div v-for="(deputations, index) in (selectedTeacher.deputations.length > 3 && !teacherDetailsDeputationExpanded ? selectedTeacher.deputations.slice(0, 3) : selectedTeacher.deputations)" :key="index" class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-pink-200 dark:border-pink-700">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label class="text-sm font-medium text-pink-700 dark:text-pink-300">Department</label>
                            <p class="text-sm text-pink-900 dark:text-pink-100">{{ deputations.department_name }}</p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-pink-700 dark:text-pink-300">Designation</label>
                            <p class="text-sm text-pink-900 dark:text-pink-100">{{ deputations.designation }}</p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-pink-700 dark:text-pink-300">Period</label>
                            <p class="text-sm text-pink-900 dark:text-pink-100">{{ formatDate(deputations.joining_date) }} - {{ deputations.end_date ? formatDate(deputations.end_date) : 'Current' }}</p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-pink-700 dark:text-pink-300">Status</label>
                            <p class="text-sm text-pink-900 dark:text-pink-100">
                              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200">
                                {{ deputations.status || 'Active' }}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- More/Less button for expandable deputation history -->
                    <div v-if="selectedTeacher.deputations.length > 3" class="flex justify-center pt-3 mt-3 border-t border-pink-200 dark:border-pink-700">
                      <button 
                        @click="teacherDetailsDeputationExpanded = !teacherDetailsDeputationExpanded"
                        class="text-sm font-medium text-pink-600 dark:text-pink-400 hover:text-pink-800 dark:hover:text-pink-300 flex items-center justify-center gap-2 px-4 py-2 rounded-lg hover:bg-pink-100 dark:hover:bg-pink-900/30 transition-colors"
                      >
                        <template v-if="!teacherDetailsDeputationExpanded">
                          <span>Show {{ selectedTeacher.deputations.length - 3 }} more deputation{{ selectedTeacher.deputations.length - 3 > 1 ? 's' : '' }}</span>
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </template>
                        <template v-else>
                          <span>Show less</span>
                          <svg class="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </template>
                      </button>
                    </div>
                  </div>

                  <!-- Attachments -->
                  <div v-if="selectedTeacher.attachments && selectedTeacher.attachments.length > 0" class="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-teal-900 dark:text-teal-100 mb-4 flex items-center">
                      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
                      </svg>
                      Attachment History
                    </h4>
                    <div class="space-y-3">
                      <div v-for="(attachments, index) in (selectedTeacher.attachments.length > 3 && !teacherDetailsAttachmentExpanded ? selectedTeacher.attachments.slice(0, 3) : selectedTeacher.attachments)" :key="index" class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-teal-200 dark:border-teal-700">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label class="text-sm font-medium text-teal-700 dark:text-teal-300">Department</label>
                            <p class="text-sm text-teal-900 dark:text-teal-100">{{ attachments.department_name }}</p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-teal-700 dark:text-teal-300">Designation</label>
                            <p class="text-sm text-teal-900 dark:text-teal-100">{{ attachments.designation }}</p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-teal-700 dark:text-teal-300">Period</label>
                            <p class="text-sm text-teal-900 dark:text-teal-100">{{ formatDate(attachments.joining_date) }} - {{ attachments.end_date ? formatDate(attachments.end_date) : 'Current' }}</p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-teal-700 dark:text-teal-300">Status</label>
                            <p class="text-sm text-teal-900 dark:text-teal-100">
                              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200">
                                {{ attachments.status || 'Active' }}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- More/Less button for expandable attachment history -->
                    <div v-if="selectedTeacher.attachments.length > 3" class="flex justify-center pt-3 mt-3 border-t border-teal-200 dark:border-teal-700">
                      <button 
                        @click="teacherDetailsAttachmentExpanded = !teacherDetailsAttachmentExpanded"
                        class="text-sm font-medium text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 flex items-center justify-center gap-2 px-4 py-2 rounded-lg hover:bg-teal-100 dark:hover:bg-teal-900/30 transition-colors"
                      >
                        <template v-if="!teacherDetailsAttachmentExpanded">
                          <span>Show {{ selectedTeacher.attachments.length - 3 }} more attachment{{ selectedTeacher.attachments.length - 3 > 1 ? 's' : '' }}</span>
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </template>
                        <template v-else>
                          <span>Show less</span>
                          <svg class="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </template>
                      </button>
                    </div>
                  </div>

                  <!-- Suspension History -->
                  <div class="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-4 flex items-center justify-between">
                      <div class="flex items-center">
                        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                        </svg>
                        Suspension History
                      </div>
                      <span v-if="suspensionCount > 0" class="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-orange-200 dark:bg-orange-800 text-orange-900 dark:text-orange-100">
                        Total: {{ suspensionCount }} {{ suspensionCount === 1 ? 'Suspension' : 'Suspensions' }}
                      </span>
                    </h4>
                    <div v-if="loadingSuspensionHistory" class="text-center py-4">
                      <div class="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow-lg rounded-lg text-white bg-gradient-to-r from-orange-500 to-orange-600">
                        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading suspension history...
                      </div>
                    </div>
                    <div v-else-if="suspensionHistory.length === 0" class="text-center py-4">
                      <p class="text-sm text-orange-700 dark:text-orange-300">No suspension history recorded</p>
                    </div>
                    <div v-else class="space-y-3">
                      <div v-for="(suspension, index) in (suspensionHistory.length > 3 && !teacherDetailsSuspensionExpanded ? suspensionHistory.slice(0, 3) : suspensionHistory)" :key="suspension.id || index" class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-orange-200 dark:border-orange-700">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Suspension Period</label>
                            <p class="text-sm text-orange-900 dark:text-orange-100 font-semibold">
                              {{ formatDate(suspension.suspension_from_date) }} - {{ formatDate(suspension.suspension_to_date) }}
                            </p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Status</label>
                            <p class="text-sm text-orange-900 dark:text-orange-100">
                              <span :class="[
                                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                                suspension.status === 'active' 
                                  ? 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200'
                                  : 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                              ]">
                                {{ suspension.status === 'active' ? 'Active Suspension' : 'Completed' }}
                              </span>
                            </p>
                          </div>
                          <div v-if="suspension.completed_at" class="md:col-span-2">
                            <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Completed At</label>
                            <p class="text-sm text-orange-900 dark:text-orange-100">{{ formatDate(suspension.completed_at) }}</p>
                          </div>
                          <div v-if="suspension.suspension_remarks" class="md:col-span-2">
                            <label class="text-sm font-medium text-orange-700 dark:text-orange-300">Remarks</label>
                            <p class="text-sm text-orange-900 dark:text-orange-100">{{ suspension.suspension_remarks }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- More/Less button for expandable suspension history -->
                    <div v-if="suspensionHistory.length > 3" class="flex justify-center pt-3 mt-3 border-t border-orange-200 dark:border-orange-700">
                      <button 
                        @click="teacherDetailsSuspensionExpanded = !teacherDetailsSuspensionExpanded"
                        class="text-sm font-medium text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 flex items-center justify-center gap-2 px-4 py-2 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                      >
                        <template v-if="!teacherDetailsSuspensionExpanded">
                          <span>Show {{ suspensionHistory.length - 3 }} more suspension{{ suspensionHistory.length - 3 > 1 ? 's' : '' }}</span>
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </template>
                        <template v-else>
                          <span>Show less</span>
                          <svg class="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </template>
                      </button>
                    </div>
                  </div>

                  <!-- Medical Records -->
                  <div class="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-red-900 dark:text-red-100 mb-4 flex items-center">
                      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                      </svg>
                      Medical Records
                      <span v-if="loadingMedicalRecords" class="ml-2">
                        <svg class="animate-spin h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </span>
                    </h4>
                    
                    <div v-if="loadingMedicalRecords" class="text-center py-4">
                      <p class="text-sm text-red-700 dark:text-red-300">Loading medical records...</p>
                    </div>
                    
                    <div v-else-if="teacherMedicalRecords.length === 0" class="text-center py-4">
                      <p class="text-sm text-red-700 dark:text-red-300">No approved medical records for this teacher.</p>
                    </div>
                    
                    <div v-else class="space-y-3">
                      <div v-for="(record, index) in (teacherMedicalRecords.length > 3 && !teacherDetailsMedicalExpanded ? teacherMedicalRecords.slice(0, 3) : teacherMedicalRecords)" :key="index" class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-red-200 dark:border-red-700">
                        <div class="flex flex-wrap items-center gap-2 mb-3">
                          <span
                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                            :class="{
                              'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100': record.approval_status === 'pending',
                              'bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-100': record.approval_status === 'rejected',
                              'bg-green-100 dark:bg-green-900/40 text-green-900 dark:text-green-100': !record.approval_status || record.approval_status === 'approved'
                            }"
                          >
                            {{ record.approval_status === 'pending' ? 'Pending review' : record.approval_status === 'rejected' ? 'Rejected' : 'Approved' }}
                          </span>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label class="text-sm font-medium text-red-700 dark:text-red-300">Ailment Name</label>
                            <p class="text-sm text-red-900 dark:text-red-100 font-semibold">{{ record.ailment_name || 'Not specified' }}</p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-red-700 dark:text-red-300">Severity</label>
                            <p class="text-sm text-red-900 dark:text-red-100">
                              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                    :class="{
                                      'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200': record.severity === 'Severe',
                                      'bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200': record.severity === 'Moderate',
                                      'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200': record.severity === 'Mild',
                                      'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200': !record.severity
                                    }">
                                {{ record.severity ?? 'Not specified' }}
                              </span>
                            </p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-red-700 dark:text-red-300">Diagnosis Date</label>
                            <p class="text-sm text-red-900 dark:text-red-100">{{ formatDate(record.diagnosis_date ?? '') }}</p>
                          </div>
                          <div>
                            <label class="text-sm font-medium text-red-700 dark:text-red-300">Treatment Status</label>
                            <p class="text-sm text-red-900 dark:text-red-100">
                              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                    :class="{
                                      'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200': record.treatment_status === 'Completed',
                                      'bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200': record.treatment_status === 'Ongoing',
                                      'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200': record.treatment_status === 'Pending',
                                      'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200': !record.treatment_status
                                    }">
                                {{ record.treatment_status || 'Not specified' }}
                              </span>
                            </p>
                          </div>
                          <div v-if="record.remarks" class="md:col-span-2">
                            <label class="text-sm font-medium text-red-700 dark:text-red-300">Remarks</label>
                            <p class="text-sm text-red-900 dark:text-red-100">{{ record.remarks }}</p>
                          </div>
                          <div v-if="record.review_remarks && record.approval_status === 'rejected'" class="md:col-span-2">
                            <label class="text-sm font-medium text-red-700 dark:text-red-300">Review note</label>
                            <p class="text-sm text-red-900 dark:text-red-100">{{ record.review_remarks }}</p>
                          </div>
                          <div v-if="record.documents" class="md:col-span-2">
                            <label class="text-sm font-medium text-red-700 dark:text-red-300">Documents</label>
                            <p class="text-sm text-red-900 dark:text-red-100">
                              <a :href="fileUrl(record.documents)" target="_blank" rel="noopener" class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline">
                                View Document
                              </a>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- More/Less button for expandable medical records -->
                    <div v-if="teacherMedicalRecords.length > 3" class="flex justify-center pt-3 mt-3 border-t border-red-200 dark:border-red-700">
                      <button 
                        @click="teacherDetailsMedicalExpanded = !teacherDetailsMedicalExpanded"
                        class="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex items-center justify-center gap-2 px-4 py-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      >
                        <template v-if="!teacherDetailsMedicalExpanded">
                          <span>Show {{ teacherMedicalRecords.length - 3 }} more record{{ teacherMedicalRecords.length - 3 > 1 ? 's' : '' }}</span>
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </template>
                        <template v-else>
                          <span>Show less</span>
                          <svg class="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </template>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:gap-3">
            <button 
              v-if="showMarkNonActiveButton && canMarkNonActive && selectedTeacher && (selectedTeacher as any).active_status !== 'non_active'"
              @click="notifyMarkNonActive"
              class="btn-danger"
            >
              Mark as Non-Active
            </button>
            <button @click="closeModalInternal" class="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

  <!-- Education Qualifications Modal -->
  <div v-if="showEducationQualificationsModal" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
    <div class="flex items-end justify-center min-h-screen min-w-0 pt-4 px-2 sm:px-4 pb-24 text-center sm:block sm:p-0 sm:pb-20">
      <div class="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-80" @click="showEducationQualificationsModal = false"></div>
      <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl dark:shadow-2xl transform transition-all sm:my-8 sm:align-middle w-full max-w-[calc(100vw-0.5rem)] sm:max-w-5xl sm:w-full max-h-[85vh] sm:max-h-[90vh] overflow-y-auto overscroll-y-contain touch-pan-y">
        <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/30 sm:mx-0 sm:h-10 sm:w-10">
              <svg class="h-6 w-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                Education Qualifications
              </h3>
              <div class="mt-4 space-y-4 max-h-[70vh] overflow-y-auto">
                <div 
                  v-for="(qualification, index) in teacherEducationQualifications" 
                  :key="qualification.id ?? index"
                  class="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-700"
                >
                  <div class="flex items-start justify-between mb-3 gap-2 flex-wrap">
                    <div class="flex-1 min-w-0">
                      <h4 class="text-lg font-bold text-amber-900 dark:text-amber-100">{{ qualification.degree_name }}</h4>
                      <p class="text-sm text-amber-700 dark:text-amber-300 mt-1">{{ qualification.qualification_type }}</p>
                    </div>
                    <div class="flex flex-col items-end gap-2">
                      <span v-if="qualification.is_verified" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200">
                        Verified
                      </span>
                      <span
                        v-else-if="qualification.admin_rejection_remarks"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200"
                      >
                        Rejected
                      </span>
                      <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-800 text-amber-900 dark:text-amber-100">
                        Pending verification
                      </span>
                      <div
                        v-if="canVerifyEducationQualifications && !qualification.is_verified"
                        class="flex flex-col sm:flex-row flex-wrap gap-2 justify-end w-full sm:w-auto"
                      >
                        <button
                          v-if="!qualification.admin_rejection_remarks"
                          type="button"
                          :disabled="verifyingEducationQualId === qualification.id || rejectingEducationQualId === qualification.id"
                          class="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 w-full sm:w-auto sm:min-w-[10rem]"
                          @click="verifyTeacherEducationQualification(qualification)"
                        >
                          {{ verifyingEducationQualId === qualification.id ? 'Verifyingâ€¦' : 'Approve qualification' }}
                        </button>
                        <button
                          type="button"
                          :disabled="verifyingEducationQualId === qualification.id || rejectingEducationQualId === qualification.id"
                          class="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 w-full sm:w-auto sm:min-w-[10rem]"
                          @click="openRejectEducationQualificationModal(qualification)"
                        >
                          {{
                            qualification.admin_rejection_remarks
                              ? 'Update rejection'
                              : 'Reject qualification'
                          }}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label class="text-xs font-medium text-amber-700 dark:text-amber-300">Institution</label>
                      <p class="text-sm text-amber-900 dark:text-amber-100 font-semibold">{{ qualification.institution_name }}</p>
                    </div>
                    <div>
                      <label class="text-xs font-medium text-amber-700 dark:text-amber-300">Year of Passing</label>
                      <p class="text-sm text-amber-900 dark:text-amber-100 font-semibold">{{ qualification.year_of_passing }}</p>
                    </div>
                    <div v-if="qualification.university_board">
                      <label class="text-xs font-medium text-amber-700 dark:text-amber-300">University/Board</label>
                      <p class="text-sm text-amber-900 dark:text-amber-100">{{ qualification.university_board }}</p>
                    </div>
                    <div v-if="qualification.percentage_cgpa">
                      <label class="text-xs font-medium text-amber-700 dark:text-amber-300">Percentage/CGPA</label>
                      <p class="text-sm text-amber-900 dark:text-amber-100 font-semibold">{{ qualification.percentage_cgpa }}</p>
                    </div>
                    <div v-if="qualification.specialization" class="md:col-span-2">
                      <label class="text-xs font-medium text-amber-700 dark:text-amber-300">Specialization</label>
                      <p class="text-sm text-amber-900 dark:text-amber-100">{{ qualification.specialization }}</p>
                    </div>
                  </div>

                  <!-- Document Links -->
                  <div v-if="qualification.marksheet_url || qualification.certificate_url" class="mt-4 pt-4 border-t border-amber-200 dark:border-amber-700">
                    <div class="flex flex-wrap gap-2">
                      <button 
                        v-if="qualification.marksheet_url" 
                        @click="viewDocument(qualification.marksheet_url, 'Marksheet - ' + qualification.degree_name)"
                        class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                      >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        View Marksheet
                      </button>
                      <button 
                        v-if="qualification.certificate_url" 
                        @click="viewDocument(qualification.certificate_url, 'Certificate - ' + qualification.degree_name)"
                        class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                      >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        View Certificate
                      </button>
                    </div>
                  </div>

                  <div
                    v-if="qualification.admin_rejection_remarks"
                    class="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-900 dark:text-red-100"
                  >
                    <strong>Admin rejection:</strong> {{ qualification.admin_rejection_remarks }}
                  </div>
                  <div v-if="qualification.remarks" class="mt-3 p-2 bg-amber-100 dark:bg-amber-800/30 rounded text-xs text-amber-800 dark:text-amber-200">
                    <strong>Teacher remarks:</strong> {{ qualification.remarks }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button @click="showEducationQualificationsModal = false" class="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Reject education qualification (remarks required) -->
  <div
    v-if="showRejectEducationQualModal"
    class="fixed inset-0 z-[60] overflow-y-auto"
    role="dialog"
    aria-modal="true"
    aria-labelledby="reject-edu-qual-title"
  >
    <div class="flex min-h-screen items-center justify-center p-4">
      <div
        class="fixed inset-0 bg-gray-900/70 dark:bg-black/80"
        @click="closeRejectEducationQualificationModal"
      ></div>
      <div
        class="relative w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-600"
      >
        <div class="px-4 pt-5 pb-4 sm:p-6">
          <h3 id="reject-edu-qual-title" class="text-lg font-medium text-gray-900 dark:text-gray-100">
            Reject qualification
          </h3>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            The teacher will see these remarks and can edit the qualification before resubmitting.
          </p>
          <label for="reject-edu-qual-remarks" class="mt-4 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Remarks <span class="text-red-600">*</span>
          </label>
          <textarea
            id="reject-edu-qual-remarks"
            v-model="rejectEducationQualRemarks"
            rows="4"
            class="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-red-500 focus:ring-red-500"
            placeholder="Explain why this qualification is being rejectedâ€¦"
          />
        </div>
        <div class="bg-gray-50 dark:bg-gray-700/80 px-4 py-3 sm:flex sm:flex-row-reverse sm:gap-2 sm:px-6 rounded-b-lg">
          <button
            type="button"
            class="btn-danger w-full sm:w-auto"
            :disabled="rejectingEducationQualId !== null"
            @click="submitRejectEducationQualification"
          >
            {{ rejectingEducationQualId !== null ? 'Rejectingâ€¦' : 'Reject qualification' }}
          </button>
          <button
            type="button"
            class="btn-secondary w-full sm:w-auto mt-2 sm:mt-0"
            :disabled="rejectingEducationQualId !== null"
            @click="closeRejectEducationQualificationModal"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Training Details Modal -->
  <div v-if="showTrainingsModal" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
    <div class="flex items-end justify-center min-h-screen min-w-0 pt-4 px-2 sm:px-4 pb-24 text-center sm:block sm:p-0 sm:pb-20">
      <div class="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-80" @click="showTrainingsModal = false"></div>
      <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl dark:shadow-2xl transform transition-all sm:my-8 sm:align-middle w-full max-w-[calc(100vw-0.5rem)] sm:max-w-5xl sm:w-full max-h-[85vh] sm:max-h-[90vh] overflow-y-auto overscroll-y-contain touch-pan-y">
        <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 dark:bg-indigo-900/30 sm:mx-0 sm:h-10 sm:w-10">
              <svg class="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                Training Details
              </h3>
              <div class="mt-4 space-y-4 max-h-[70vh] overflow-y-auto">
                <div 
                  v-for="(training, index) in teacherTrainings" 
                  :key="index"
                  class="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700"
                >
                  <div class="flex items-start justify-between mb-3">
                    <div class="flex-1">
                      <h4 class="text-lg font-bold text-indigo-900 dark:text-indigo-100">{{ training.training_name }}</h4>
                      <p class="text-sm text-indigo-700 dark:text-indigo-300 mt-1">{{ training.provider_name }}</p>
                    </div>
                  </div>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label class="text-xs font-medium text-indigo-700 dark:text-indigo-300">Provider</label>
                      <p class="text-sm text-indigo-900 dark:text-indigo-100 font-semibold">{{ training.provider_name }}</p>
                    </div>
                    <div>
                      <label class="text-xs font-medium text-indigo-700 dark:text-indigo-300">Start Date</label>
                      <p class="text-sm text-indigo-900 dark:text-indigo-100 font-semibold">{{ formatTrainingDate(training.start_date) }}</p>
                    </div>
                    <div v-if="training.end_date">
                      <label class="text-xs font-medium text-indigo-700 dark:text-indigo-300">End Date</label>
                      <p class="text-sm text-indigo-900 dark:text-indigo-100 font-semibold">{{ formatTrainingDate(training.end_date) }}</p>
                    </div>
                    <div v-if="training.duration">
                      <label class="text-xs font-medium text-indigo-700 dark:text-indigo-300">Duration</label>
                      <p class="text-sm text-indigo-900 dark:text-indigo-100 font-semibold">{{ training.duration }}</p>
                    </div>
                    <div v-if="training.training_type">
                      <label class="text-xs font-medium text-indigo-700 dark:text-indigo-300">Type</label>
                      <p class="text-sm text-indigo-900 dark:text-indigo-100 font-semibold">{{ training.training_type }}</p>
                    </div>
                    <div v-if="training.description" class="md:col-span-2">
                      <label class="text-xs font-medium text-indigo-700 dark:text-indigo-300">Description</label>
                      <p class="text-sm text-indigo-900 dark:text-indigo-100">{{ training.description }}</p>
                    </div>
                  </div>

                  <!-- Certificate Link -->
                  <div v-if="training.certificate_url" class="mt-4 pt-4 border-t border-indigo-200 dark:border-indigo-700">
                    <button 
                      @click="viewDocument(training.certificate_url, 'Training Certificate - ' + training.training_name)"
                      class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                    >
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      View Certificate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button @click="showTrainingsModal = false" class="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Document Viewer Modal -->
  <div v-if="showDocumentModal && selectedDocumentUrl" class="fixed inset-0 bg-gray-600 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80 overflow-y-auto h-full w-full z-[60] p-4" @click.self="closeDocumentModal">
    <div class="relative top-4 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-5xl shadow-2xl rounded-xl bg-white dark:bg-gray-800">
      <div class="mt-3">
        <div class="flex justify-between items-center mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <svg class="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
            </svg>
            {{ selectedDocumentTitle }}
          </h3>
          <div class="flex items-center gap-2">
            <a 
              :href="selectedDocumentUrl" 
              target="_blank"
              download
              class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              title="Download"
            >
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              Download
            </a>
            <button
              @click="closeDocumentModal"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Close"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
        
        <div class="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 min-h-[500px] max-h-[70vh] overflow-auto">
          <!-- PDF Viewer -->
          <iframe
            v-if="isPdfDocument(selectedDocumentUrl)"
            :src="getDocumentPreviewUrl(selectedDocumentUrl)"
            class="w-full h-full min-h-[500px] border-0 rounded-lg"
            frameborder="0"
          ></iframe>
          <!-- Image Viewer -->
          <div v-else-if="isImageDocument(selectedDocumentUrl)" class="flex items-center justify-center h-full min-h-[500px]">
            <img
              :src="getDocumentPreviewUrl(selectedDocumentUrl)"
              :alt="selectedDocumentTitle"
              class="max-w-full max-h-full object-contain rounded-lg shadow-lg"
              @error="handleDocumentError"
            />
          </div>
          <!-- Unsupported file type -->
          <div v-else class="flex items-center justify-center h-full min-h-[500px] text-gray-500 dark:text-gray-400">
            <div class="text-center">
              <svg class="w-20 h-20 mx-auto mb-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <p class="text-lg font-semibold mb-2">Preview not available</p>
              <p class="text-sm mb-4">This file type cannot be previewed in the browser.</p>
              <a 
                :href="selectedDocumentUrl" 
                target="_blank"
                download
                class="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                Download to view
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Change Appointment Type Modal -->
  <div v-if="showChangeAppointmentTypeModal" class="fixed inset-0 z-[60] overflow-y-auto" role="dialog" aria-modal="true">
    <div class="flex min-h-screen items-end justify-center px-4 pb-20 pt-6 text-center sm:block sm:p-0">
      <div
        class="fixed inset-0 bg-gray-900/50 backdrop-blur-[2px] transition-opacity dark:bg-gray-950/70"
        @click="closeChangeAppointmentTypeModal"
      ></div>
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
      <div
        class="inline-block w-full transform overflow-hidden rounded-2xl bg-white text-left align-bottom shadow-2xl ring-1 ring-black/5 transition-all dark:bg-gray-900 sm:my-8 sm:max-w-lg sm:align-middle"
      >
        <div class="flex items-start gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-800 sm:px-6">
          <div class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-200">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6l4 2" />
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">Change Appointment Type</h3>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              This action is audited and may split the active posting history.
            </p>
          </div>
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            :disabled="changeAppointmentTypeSaving"
            @click="closeChangeAppointmentTypeModal"
            aria-label="Close"
          >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="space-y-5 px-5 py-5 sm:px-6">
          <div>
            <label class="form-label">New appointment type</label>
            <select
              v-model="changeAppointmentTypeNewTypeId"
              class="mt-1 w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-primary-400 dark:focus:ring-primary-400/15"
              :disabled="loadingAppointmentTypes || changeAppointmentTypeSaving"
            >
              <option :value="null">Select…</option>
              <option v-for="t in appointmentTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
            <p v-if="loadingAppointmentTypes" class="mt-1 text-xs text-gray-500 dark:text-gray-400">Loading appointment types…</p>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label class="form-label">Effective date</label>
            <input
              v-model="changeAppointmentTypeEffectiveDate"
              type="date"
              class="mt-1 w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-primary-400 dark:focus:ring-primary-400/15"
              :min="minChangeAppointmentTypeEffectiveDate || undefined"
              :disabled="changeAppointmentTypeSaving"
            />
            <p v-if="minChangeAppointmentTypeEffectiveDate" class="mt-1 text-xs text-gray-500">
              Must be on/after {{ minChangeAppointmentTypeEffectiveDate }} (last appointment type effective date).
            </p>
            </div>
            <div>
              <label class="form-label">School (School ID)</label>
              <input
                v-model="changeAppointmentTypeSchoolId"
                type="text"
                class="mt-1 w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-primary-400 dark:focus:ring-primary-400/15"
                placeholder="e.g. 15010102404"
                :disabled="changeAppointmentTypeSaving"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Default is the teacher’s current school. Override only if needed for audit.
              </p>
            </div>
          </div>

          <div>
            <label class="form-label">Order number <span class="text-gray-400">(optional)</span></label>
            <input
              v-model="changeAppointmentTypeOrderNumber"
              type="text"
              class="mt-1 w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-primary-400 dark:focus:ring-primary-400/15"
              :disabled="changeAppointmentTypeSaving"
            />
          </div>

          <div>
            <label class="form-label">Remarks <span class="text-gray-400">(optional)</span></label>
            <textarea
              v-model="changeAppointmentTypeRemarks"
              class="mt-1 w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-primary-400 dark:focus:ring-primary-400/15"
              rows="4"
              :disabled="changeAppointmentTypeSaving"
              placeholder="Add remarks for audit trail (if any)"
            ></textarea>
          </div>
        </div>

        <div class="flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4 dark:border-gray-800 dark:bg-gray-900/40 sm:px-6">
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-primary-500/15 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-750"
            :disabled="changeAppointmentTypeSaving"
            @click="closeChangeAppointmentTypeModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-primary-500 dark:hover:bg-primary-400"
            :disabled="changeAppointmentTypeSaving"
            @click="submitChangeAppointmentType"
          >
            {{ changeAppointmentTypeSaving ? 'Saving…' : 'Confirm change' }}
          </button>
        </div>
      </div>
    </div>
  </div>

</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import type { Teacher } from '../types'
import { teachersApi, medicalRecordsApi, priorityBadgesApi, type MedicalRecord } from '../services/api'
import { simpleProfileApi } from '../services/api'
import api from '../services/api/axios'
import logger from '../utils/logger'
import { useAuthStore } from '../stores/auth'
import { canApproveEducationQualificationRole } from '../constants/roles'
import { EDUCATION_QUALIFICATIONS_CHANGED_EVENT } from '../utils/educationQualificationsUiEvents'
import { notifySuccess, notifyError } from '../utils/notifications'
import { appointmentTypesApi } from '../services/api/master-data/appointmentTypes'
import {
  registerTeacherDetailsModalHandlers,
  unregisterTeacherDetailsModalHandlers,
  type OpenTeacherDetailsOptions,
} from '../utils/teacherDetailsModalLauncher'
import { formatDateOnlyForDisplay } from '../utils/dateOnlyDisplay'
import { resolveTeacherProfilePictureSource } from '../utils/profilePicture'
import { toUploadUrl } from '../utils/uploadUrl'

/** Date-only normalization aligned with Posting History full-timeline attribution. */
function normalizeAppointmentTimelineDate(value: unknown): Date | null {
  if (value == null || value === '') return null
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const d = new Date(`${value.slice(0, 10)}T00:00:00.000Z`)
    return isNaN(d.getTime()) ? null : d
  }
  const d = new Date(value as string)
  return isNaN(d.getTime()) ? null : d
}

function schoolNameForAppointmentHistoryRow(row: any, teacher: Teacher | null): string {
  if (!teacher) return 'Unknown school'
  const postings = [...(teacher.posting_histories || [])] as any[]
  const annotated = postings
    .map((p) => {
      const status = String(p?.status || '').trim()
      const startRaw = status === 'Active' ? (p.effective_date ?? p.from_date) : p.from_date
      const start = normalizeAppointmentTimelineDate(startRaw)
      return start ? { p, start } : null
    })
    .filter((x): x is { p: any; start: Date } => x != null)
    .sort((a, b) => a.start.getTime() - b.start.getTime())

  const d = normalizeAppointmentTimelineDate(row?.change_date)
  if (d && annotated.length) {
    if (d.getTime() < annotated[0].start.getTime()) {
      return String(annotated[0].p?.school_name || '').trim() || 'Unknown school'
    }
    for (let i = 0; i < annotated.length; i++) {
      const start = annotated[i].start
      const nextStart = i + 1 < annotated.length ? annotated[i + 1].start : null
      if (d.getTime() >= start.getTime() && (!nextStart || d.getTime() < nextStart.getTime())) {
        return String(annotated[i].p?.school_name || '').trim() || 'Unknown school'
      }
    }
  }
  const sid = row?.school_id != null ? String(row.school_id).trim() : ''
  if (sid) {
    const hit = postings.find(
      (p: any) =>
        String(p?.from_school_id || '').trim() === sid ||
        String(p?.to_school_id || '').trim() === sid ||
        String(p?.school_id || '').trim() === sid
    )
    if (hit?.school_name) return String(hit.school_name).trim()
  }
  const cur = String((teacher as any)?.current_school_name || '').trim()
  if (cur) return cur
  return 'Unknown school'
}

const auth = useAuthStore()
const router = useRouter()

/** After closing the main modal, navigate here if still a safe in-app path. */
const returnToAfterClose = ref<string | null>(null)

const modalOpen = ref(false)
const showMarkNonActiveButton = ref(false)
const selectedTeacher = ref<Teacher | null>(null)
const selectedTeacherProfilePicture = ref<string | null>(null)
const profilePictureBroken = ref(false)
const showTeacherProfilePhoto = computed(
  () => !!selectedTeacherProfilePicture.value && !profilePictureBroken.value
)
const teacherProfilePhotoSrc = computed(() =>
  toUploadUrl(selectedTeacherProfilePicture.value)
)

function onTeacherProfilePhotoLoad(event: Event) {
  const img = event.target as HTMLImageElement | null
  if (img && (img.naturalWidth <= 2 || img.naturalHeight <= 2)) {
    profilePictureBroken.value = true
  }
}
const teacherMedicalRecords = ref<MedicalRecord[]>([])
const loadingMedicalRecords = ref(false)
const teacherEducationQualifications = ref<any[]>([])
const loadingEducationQualifications = ref(false)
const teacherTrainings = ref<any[]>([])
const loadingTrainings = ref(false)
const teacherPriorityBadges = ref<any[]>([])
const teacherMLABadges = ref<any[]>([])
const mlaBadgeMenuEnabled = ref(false)
const suspensionHistory = ref<any[]>([])
const suspensionCount = ref(0)
const loadingSuspensionHistory = ref(false)

const teacherDetailsPostingHistoryExpanded = ref(false)
const teacherDetailsTrainingExpanded = ref(false)
const teacherDetailsBadgesExpanded = ref(false)
const teacherDetailsAttachmentExpanded = ref(false)
const teacherDetailsSuspensionExpanded = ref(false)
const teacherDetailsMedicalExpanded = ref(false)
const teacherDetailsDeputationExpanded = ref(false)

const showEducationQualificationsModal = ref(false)
const showTrainingsModal = ref(false)
const showDocumentModal = ref(false)
const selectedDocumentUrl = ref<string | null>(null)
const selectedDocumentTitle = ref('')

const showRejectEducationQualModal = ref(false)
const rejectEducationQualId = ref<number | null>(null)
const rejectEducationQualRemarks = ref('')
const rejectingEducationQualId = ref<number | null>(null)
const verifyingEducationQualId = ref<number | null>(null)
const selectedQualification = ref<any>(null)

const canVerifyEducationQualifications = computed(() =>
  canApproveEducationQualificationRole(auth.currentUser?.role)
)

const canMarkNonActive = computed(() => {
  const role = auth.currentUser?.role
  return ['super_admin', 'admin_directorate', 'admin_secretariat'].includes(role || '')
})

const canChangeAppointmentType = computed(() => {
  const role = auth.currentUser?.role
  return ['super_admin', 'admin_directorate', 'admin_secretariat'].includes(role || '')
})

const appointmentTypeHistory = ref<any[]>([])
const loadingAppointmentTypeHistory = ref(false)

/** Same rows / layout as PostingHistory "Full appointment type timeline" (excludes carry_forward; anchors first date to first posting). */
const appointmentTypeTimelineDisplayRows = computed(() => {
  const teacher = selectedTeacher.value
  const raw = appointmentTypeHistory.value || []
  const rows = raw
    .filter((r: any) => String(r?.remarks || '').trim() !== 'carry_forward')
    .filter((r: any) => r?.change_date)
    .map((r: any) => ({
      id: r.id,
      name: r?.new_type?.name || 'Unknown',
      date: r.change_date,
      schoolName: schoolNameForAppointmentHistoryRow(r, teacher),
      source: String(r?.remarks || '').trim() || '—',
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  const postings = [...(teacher?.posting_histories || [])] as any[]
  let firstStart: Date | null = null
  if (postings.length) {
    const p0 = postings[0]
    const status0 = String(p0?.status || '').trim()
    const startRaw0 = status0 === 'Active' ? (p0.effective_date ?? p0.from_date) : p0.from_date
    firstStart = normalizeAppointmentTimelineDate(startRaw0)
  }

  if (firstStart && rows.length) {
    const ymd = firstStart.toISOString().slice(0, 10)
    const copy = [...rows]
    copy[0] = { ...copy[0], date: `${ymd}T00:00:00.000Z` }
    return copy
  }
  return rows
})

const showChangeAppointmentTypeModal = ref(false)
const changeAppointmentTypeSaving = ref(false)
const appointmentTypes = ref<Array<{ id: number; name: string }>>([])
const loadingAppointmentTypes = ref(false)

const changeAppointmentTypeNewTypeId = ref<number | null>(null)
const changeAppointmentTypeEffectiveDate = ref<string>('')
const changeAppointmentTypeSchoolId = ref<string>('')
const changeAppointmentTypeOrderNumber = ref<string>('')
const changeAppointmentTypeRemarks = ref<string>('')

const minChangeAppointmentTypeEffectiveDate = computed(() => {
  const rows = appointmentTypeHistory.value || []
  let best: string | null = null
  for (const r of rows) {
    const raw = (r as any)?.change_date
    if (!raw) continue
    const s =
      typeof raw === 'string'
        ? raw.slice(0, 10)
        : (() => {
            const d = new Date(raw)
            return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
          })()
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) continue
    if (!best || s > best) best = s
  }
  return best
})

const selectedTeacherProfessionalQualificationLabels = computed(() => {
  const t = selectedTeacher.value as any
  if (!t) return []
  const raw = t.professional_qualification
  if (raw != null && String(raw).trim() !== '') {
    return String(raw)
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean)
  }
  const single = t.professional_qualification_types?.name
  return single ? [String(single)] : []
})

const reloadTeacherEducationQualifications = async () => {
  const tid = selectedTeacher.value?.id
  if (!tid) return
  try {
    const qualificationsResp = await simpleProfileApi.getQualifications(tid)
    if (qualificationsResp.data.success && qualificationsResp.data.data) {
      teacherEducationQualifications.value = qualificationsResp.data.data
    }
  } catch (e) {
    logger.error('Error reloading qualifications:', e)
  }
}

const loadAppointmentTypeHistory = async (teacherId: number) => {
  loadingAppointmentTypeHistory.value = true
  try {
    const resp = await api.get(`/appointment-types/history/${teacherId}`)
    if (resp.data?.success) {
      appointmentTypeHistory.value = Array.isArray(resp.data?.data) ? resp.data.data : []
    } else {
      appointmentTypeHistory.value = []
    }
  } catch (e) {
    logger.error('Failed to load appointment type history:', e)
    appointmentTypeHistory.value = []
  } finally {
    loadingAppointmentTypeHistory.value = false
  }
}

const openChangeAppointmentTypeModal = async () => {
  const t = selectedTeacher.value as any
  if (!t?.id) return

  showChangeAppointmentTypeModal.value = true
  changeAppointmentTypeNewTypeId.value = null
  // Clamp default effective date to the latest allowed (>= last history effective date).
  const today = new Date().toISOString().slice(0, 10)
  const min = minChangeAppointmentTypeEffectiveDate.value
  changeAppointmentTypeEffectiveDate.value = min && today < min ? min : today
  changeAppointmentTypeSchoolId.value = String(t.school_id || '').trim()
  changeAppointmentTypeOrderNumber.value = ''
  changeAppointmentTypeRemarks.value = ''

  loadingAppointmentTypes.value = true
  try {
    const resp = await appointmentTypesApi.getActive()
    if (resp.data?.success && Array.isArray(resp.data?.data)) {
      appointmentTypes.value = resp.data.data
    } else {
      appointmentTypes.value = []
    }
  } catch (e) {
    logger.error('Failed to load appointment types:', e)
    appointmentTypes.value = []
  } finally {
    loadingAppointmentTypes.value = false
  }
}

const closeChangeAppointmentTypeModal = () => {
  if (changeAppointmentTypeSaving.value) return
  showChangeAppointmentTypeModal.value = false
}

const submitChangeAppointmentType = async () => {
  const t = selectedTeacher.value as any
  const teacherId = t?.id
  if (!teacherId) return

  const newTypeId = changeAppointmentTypeNewTypeId.value
  if (!newTypeId) {
    notifyError('Please select new appointment type')
    return
  }

  const effective = changeAppointmentTypeEffectiveDate.value
  if (!effective) {
    notifyError('Effective date is required')
    return
  }
  const min = minChangeAppointmentTypeEffectiveDate.value
  if (min && effective < min) {
    notifyError(`Effective date cannot be before ${min}`)
    return
  }

  const schoolId = changeAppointmentTypeSchoolId.value.trim()
  if (!schoolId) {
    notifyError('School is required')
    return
  }

  changeAppointmentTypeSaving.value = true
  try {
    const payload = {
      teacher_id: teacherId,
      new_type_id: newTypeId,
      change_date: effective,
      school_id: schoolId,
      order_number: changeAppointmentTypeOrderNumber.value.trim() || undefined,
      remarks: changeAppointmentTypeRemarks.value.trim() || undefined,
    }
    const resp = await api.post('/appointment-types/change', payload)
    if (resp.data?.success) {
      notifySuccess('Appointment type changed')
      showChangeAppointmentTypeModal.value = false
      // Notify other screens (e.g. Posting History) to refresh appointment type timeline caches.
      window.dispatchEvent(new CustomEvent('tms:appointment-type-history-updated', { detail: { teacherId } }))
      await loadTeacherDetails({ ...(selectedTeacher.value as any) })
      await loadAppointmentTypeHistory(teacherId)
    } else {
      notifyError(resp.data?.message || 'Failed to change appointment type')
    }
  } catch (e: any) {
    notifyError(e?.response?.data?.message || 'Failed to change appointment type')
  } finally {
    changeAppointmentTypeSaving.value = false
  }
}

const verifyTeacherEducationQualification = async (qualification: any) => {
  if (!qualification?.id) return
  verifyingEducationQualId.value = qualification.id
  try {
    const resp = await simpleProfileApi.verifyQualification(qualification.id)
    if (resp.data?.success) {
      notifySuccess('Education qualification verified')
      await reloadTeacherEducationQualifications()
    } else {
      notifyError((resp.data as any)?.message || 'Verification failed')
    }
  } catch (e: any) {
    notifyError(e?.response?.data?.message || 'Verification failed')
  } finally {
    verifyingEducationQualId.value = null
  }
}

const openRejectEducationQualificationModal = (qualification: any) => {
  if (!qualification?.id) return
  rejectEducationQualId.value = qualification.id
  rejectEducationQualRemarks.value = ''
  showRejectEducationQualModal.value = true
}

const closeRejectEducationQualificationModal = () => {
  if (rejectingEducationQualId.value !== null) return
  showRejectEducationQualModal.value = false
  rejectEducationQualId.value = null
  rejectEducationQualRemarks.value = ''
}

const submitRejectEducationQualification = async () => {
  const id = rejectEducationQualId.value
  const remarks = rejectEducationQualRemarks.value.trim()
  if (!id) return
  if (!remarks) {
    notifyError('Rejection remarks are required')
    return
  }
  rejectingEducationQualId.value = id
  try {
    const resp = await simpleProfileApi.rejectQualification(id, remarks)
    if (resp.data?.success) {
      notifySuccess('Qualification rejected')
      showRejectEducationQualModal.value = false
      rejectEducationQualId.value = null
      rejectEducationQualRemarks.value = ''
      await reloadTeacherEducationQualifications()
      window.dispatchEvent(new CustomEvent(EDUCATION_QUALIFICATIONS_CHANGED_EVENT))
    } else {
      notifyError((resp.data as any)?.message || 'Rejection failed')
    }
  } catch (e: any) {
    notifyError(e?.response?.data?.message || 'Rejection failedd')
  } finally {
    rejectingEducationQualId.value = null
  }
}

const checkMLABadgeMenuStatus = async () => {
  try {
    const response = await api.get('/menu')
    const menuItems = response.data?.data || []
    const mlaBadgeMenu = menuItems.find((item: any) => {
      const label = (item.label || '').toLowerCase()
      return label.includes('mla') && label.includes('approval')
    })
    if (mlaBadgeMenu && mlaBadgeMenu.is_active !== false) {
      mlaBadgeMenuEnabled.value = true
    } else {
      mlaBadgeMenuEnabled.value = false
    }
  } catch (error) {
    logger.error('Error checking MLA menu status', { err: error })
    mlaBadgeMenuEnabled.value = false
  }
}

const resolveTeacherProfilePicture = (teacherLike: Teacher | Record<string, unknown> | null | undefined) =>
  resolveTeacherProfilePictureSource(teacherLike as Record<string, unknown> | null | undefined)

const loadTeacherDetails = async (teacher: Teacher) => {
  profilePictureBroken.value = false
  selectedTeacherProfilePicture.value = resolveTeacherProfilePicture(teacher)

  if (teacher.id) {
    try {
      const profileResponse = await simpleProfileApi.getProfilePicture(teacher.id)
      if (profileResponse.data.success && profileResponse.data.data) {
        const fromApi =
          profileResponse.data.data.url || profileResponse.data.data.file_path || null
        if (fromApi) {
          selectedTeacherProfilePicture.value = fromApi
          profilePictureBroken.value = false
        }
      }
    } catch (error) {
      logger.debug('No profile picture found for teacher:', error)
    }
  }

  if (teacher.id) {
    try {
      const fullTeacherResponse = await teachersApi.getById(teacher.id)
      if (fullTeacherResponse.data.success && fullTeacherResponse.data.data) {
        selectedTeacher.value = {
          ...teacher,
          ...fullTeacherResponse.data.data,
        }
        if (!selectedTeacherProfilePicture.value) {
          selectedTeacherProfilePicture.value = resolveTeacherProfilePicture(
            fullTeacherResponse.data.data as Teacher
          )
        }
      }
    } catch (error) {
      logger.error('Error fetching full teacher details:', error)
    }
  }

  if (teacher.id) {
    loadAppointmentTypeHistory(teacher.id).catch((e) =>
      logger.error('Error loading appointment type history:', e)
    )
  }

  const teacherForMedical = selectedTeacher.value || teacher
  const medicalLookupId =
    teacherForMedical.id != null && teacherForMedical.id !== undefined
      ? teacherForMedical.id
      : teacherForMedical.teacher_ID

  if (medicalLookupId != null && medicalLookupId !== '') {
    loadingMedicalRecords.value = true
    try {
      const resp = await medicalRecordsApi.getByTeacher(medicalLookupId, { approvedOnly: true })
      const payload = resp.data as { data?: MedicalRecord[] } | undefined
      teacherMedicalRecords.value = Array.isArray(payload?.data) ? payload.data : []
    } catch (error) {
      logger.error('Error fetching medical records:', error)
      teacherMedicalRecords.value = []
    } finally {
      loadingMedicalRecords.value = false
    }
  } else {
    teacherMedicalRecords.value = []
  }

  if (teacher.teacher_ID || teacher.id) {
    if (teacher.id) {
      loadingSuspensionHistory.value = true
      try {
        const [historyResp, countResp] = await Promise.all([
          teachersApi.getSuspensionHistory(teacher.id),
          teachersApi.getSuspensionCount(teacher.id),
        ])
        if (historyResp.data.success && historyResp.data.data) {
          suspensionHistory.value = historyResp.data.data
        } else {
          suspensionHistory.value = []
        }
        if (countResp.data.success && countResp.data.data) {
          suspensionCount.value = countResp.data.data.suspension_count || 0
        } else {
          suspensionCount.value = 0
        }
      } catch (error) {
        logger.error('Error fetching suspension history:', error)
        suspensionHistory.value = []
        suspensionCount.value = 0
      } finally {
        loadingSuspensionHistory.value = false
      }
    }

    if (teacher.id) {
      try {
        const priorityResp = await priorityBadgesApi.getByTeacherId(teacher.id)
        if (priorityResp.data.success) {
          teacherPriorityBadges.value = (priorityResp.data.data || []).filter(
            (badge: any) => badge.status === 'approved'
          )
        }
      } catch (error) {
        logger.error('Error fetching priority badges:', error)
        teacherPriorityBadges.value = []
      }
    }

    if (teacher.id) {
      try {
        const mlaResp = await simpleProfileApi.getMLABadge(teacher.id)
        if (mlaResp.data.success && mlaResp.data.data) {
          const badges = Array.isArray(mlaResp.data.data) ? mlaResp.data.data : [mlaResp.data.data]
          teacherMLABadges.value = badges.filter((badge: any) => badge.status === 'approved')
        }
      } catch (error) {
        logger.error('Error fetching MLA spouse badges:', error)
        teacherMLABadges.value = []
      }
    }

    if (teacher.id) {
      loadingEducationQualifications.value = true
      try {
        const qualificationsResp = await simpleProfileApi.getQualifications(teacher.id)
        if (qualificationsResp.data.success && qualificationsResp.data.data) {
          teacherEducationQualifications.value = qualificationsResp.data.data
        } else {
          teacherEducationQualifications.value = []
        }
      } catch (error) {
        logger.error('Error fetching education qualifications:', error)
        teacherEducationQualifications.value = []
      } finally {
        loadingEducationQualifications.value = false
      }
    }

    if (teacher.id) {
      loadingTrainings.value = true
      try {
        const trainingsResp = await simpleProfileApi.getTrainings(teacher.id)
        if (trainingsResp.data.success && trainingsResp.data.data) {
          teacherTrainings.value = trainingsResp.data.data
        } else {
          teacherTrainings.value = []
        }
      } catch (error) {
        logger.error('Error fetching training details:', error)
        teacherTrainings.value = []
      } finally {
        loadingTrainings.value = false
      }
    }
  }
}

function normalizeReturnTo(path: string | undefined): string | null {
  if (!path || typeof path !== 'string') return null
  const p = path.trim()
  if (!p.startsWith('/') || p.startsWith('//')) return null
  return p
}

async function openModal(teacher: Teacher, options?: OpenTeacherDetailsOptions) {
  returnToAfterClose.value = normalizeReturnTo(options?.returnTo)
  showMarkNonActiveButton.value = options?.allowMarkNonActive !== false
  await checkMLABadgeMenuStatus()
  teacherDetailsPostingHistoryExpanded.value = false
  teacherDetailsTrainingExpanded.value = false
  teacherDetailsBadgesExpanded.value = false
  teacherDetailsAttachmentExpanded.value = false
  teacherDetailsSuspensionExpanded.value = false
  teacherDetailsMedicalExpanded.value = false
  teacherDetailsDeputationExpanded.value = false
  teacherMedicalRecords.value = []
  selectedTeacher.value = teacher
  modalOpen.value = true
  await loadTeacherDetails(teacher)
}

function closeModalInternal() {
  const navigateTo = returnToAfterClose.value
  returnToAfterClose.value = null
  modalOpen.value = false
  selectedTeacher.value = null
  selectedTeacherProfilePicture.value = null
  profilePictureBroken.value = false
  teacherMedicalRecords.value = []
  teacherEducationQualifications.value = []
  teacherTrainings.value = []
  teacherPriorityBadges.value = []
  teacherMLABadges.value = []
  showEducationQualificationsModal.value = false
  showTrainingsModal.value = false
  showDocumentModal.value = false
  selectedDocumentUrl.value = null
  selectedDocumentTitle.value = ''
  selectedQualification.value = null
  suspensionHistory.value = []
  suspensionCount.value = 0
  showRejectEducationQualModal.value = false
  rejectEducationQualId.value = null
  rejectEducationQualRemarks.value = ''
  if (navigateTo && router.currentRoute.value.fullPath !== navigateTo) {
    router.push(navigateTo)
  }
}

function notifyMarkNonActive() {
  if (selectedTeacher.value) {
    window.dispatchEvent(new CustomEvent('tms:teacher-mark-non-active', { detail: selectedTeacher.value }))
    closeModalInternal()
  }
}

const formatDisplayText = (text: string): string => {
  if (!text) return ''
  return text.replace(/_/g, ' ')
}

const getExperienceYears = (teacher: Teacher): string => {
  if (!teacher.joining_date) return 'Not specified'
  const joiningDate = new Date(teacher.joining_date)
  const currentDate = new Date()
  const diffTime = Math.abs(currentDate.getTime() - joiningDate.getTime())
  const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365.25))
  if (diffYears === 0) {
    const diffMonths = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30.44))
    return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`
  }
  return `${diffYears} year${diffYears !== 1 ? 's' : ''}`
}

const formatSubjects = (subjectsString: string): string => {
  if (!subjectsString) return ''
  try {
    const subjects = JSON.parse(subjectsString)
    return Array.isArray(subjects) ? subjects.join(', ') : subjectsString
  } catch {
    return subjectsString
  }
}

const formatClasses = (classesString: string): string => {
  if (!classesString) return ''
  try {
    const classes = JSON.parse(classesString)
    return Array.isArray(classes) ? classes.join(', ') : classesString
  } catch {
    return classesString
  }
}

const fileUrl = (url?: string | null) => {
  if (!url) return '#'
  if (/^https?:\/\//i.test(url)) return url
  let filename = url
  if (url.startsWith('/uploads/medical-records/')) {
    filename = url.replace('/uploads/medical-records/', '')
  } else if (url.startsWith('/uploads/')) {
    filename = url.replace('/uploads/', '')
  }
  return `/api/uploads/medical-records/${filename}`
}

const formatDate = (dateString: string) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString()
}

const viewEducationQualification = (qualification: any) => {
  selectedQualification.value = qualification
  showEducationQualificationsModal.value = true
}

const viewTraining = (_training: any) => {
  showTrainingsModal.value = true
}

const formatTrainingDate = (date: string | Date | null | undefined) => {
  if (!date) return 'N/A'
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return 'N/A'
    return dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return 'N/A'
  }
}

const viewDocument = (url: string, title: string) => {
  selectedDocumentUrl.value = url
  selectedDocumentTitle.value = title
  showDocumentModal.value = true
}

const closeDocumentModal = () => {
  showDocumentModal.value = false
  selectedDocumentUrl.value = null
  selectedDocumentTitle.value = ''
}

const isPdfDocument = (url: string): boolean => {
  if (!url) return false
  const lowerUrl = url.toLowerCase()
  return lowerUrl.endsWith('.pdf') || lowerUrl.includes('.pdf') || lowerUrl.includes('application/pdf')
}

const isImageDocument = (url: string): boolean => {
  if (!url) return false
  const lowerUrl = url.toLowerCase()
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
  const imageMimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/svg']
  return (
    imageExtensions.some((ext) => lowerUrl.includes(ext)) ||
    imageMimeTypes.some((mime) => lowerUrl.includes(mime))
  )
}

const getDocumentPreviewUrl = (url: string): string => {
  if (!url) return ''
  let filename = ''
  if (url.includes('/uploads/training-documents/')) {
    filename = url.replace('/uploads/training-documents/', '')
    return `/api/uploads/training-documents/${filename}`
  }
  if (url.includes('/uploads/education-documents/')) {
    filename = url.replace('/uploads/education-documents/', '')
    return `/api/uploads/education-documents/${filename}`
  }
  if (url.includes('/uploads/medical-records/')) {
    filename = url.replace('/uploads/medical-records/', '')
    return `/api/uploads/medical-records/${filename}`
  }
  if (url.includes('qual_') && !url.includes('/uploads/')) {
    filename = url.split('/').pop() || url
    return `/api/uploads/training-documents/${filename}`
  }
  let relativeUrl = url
  if (url.startsWith('http://localhost:5004')) {
    relativeUrl = url.replace('http://localhost:5004', '')
  } else if (url.startsWith('http://') && !url.startsWith('http://localhost')) {
    return url
  } else if (!url.startsWith('/')) {
    relativeUrl = '/' + url
  }
  return relativeUrl
}

const handleDocumentError = (event: Event) => {
  const url = selectedDocumentUrl.value
  logger.warn('Failed to load document:', url)
  const filename = url?.split('/').pop() || 'unknown'
  logger.error(`Document not found: ${filename}. URL: ${url}`)
  const img = event.target as HTMLImageElement
  if (img) {
    img.style.display = 'none'
    const errorDiv = document.createElement('div')
    errorDiv.className = 'text-center p-4 text-red-600 dark:text-red-400'
    errorDiv.textContent = `Document not found: ${filename}`
    img.parentElement?.appendChild(errorDiv)
  }
}

onMounted(() => {
  registerTeacherDetailsModalHandlers(openModal, closeModalInternal)
})
onUnmounted(() => {
  unregisterTeacherDetailsModalHandlers(openModal, closeModalInternal)
})

</script>
